// @flow
import * as React from 'react';
import io from 'socket.io-client';
import { toast } from 'react-toastify';
import DailyIframe from '@daily-co/daily-js';

import Text from 'components/Text';
import * as auctionService from 'services/auction';
import { withCredentials, hasRole, ROLES } from 'containers/AuthenticationProvider';
import type { User } from 'domain/User';
import type { Lot } from 'domain/Lot';
import type { Auction } from 'domain/Auction';

// $FlowFixMe
import gunSound from 'sounds/ricochet.mp3';

import classes from './style.module.scss';

const EVENT_TYPES = {
  LIVE_USERS: 'liveUsers',
  LOTS: 'lots',
  AUCTION: 'auction',
  CURRENT_LOT: 'currentLot',
  BID_PLACED: 'bidPlaced',
  LOT_STATUS_CHANGED: 'lotStatusChanged',
  STREAM_STARTED: 'streamStarted',
  STREAM_STOPPED: 'streamStopped',
};

export const STREAM_STATUS = {
  LOADING: 'LOADING',
  NO_STREAM: 'NO_STREAM',
  REQUIRES_INTERACTION: 'REQUIRES_INTERACTION',
  CONNECTING: 'CONNECTING',
  LISTENING: 'LISTENING',
  BROADCASTING: 'BROADCASTING',
  ADMIN_LISTENING: 'ADMIN_LISTENING',
};

type Props = {
  token: string,
  user: User,
  preloadedStreamUrl: string | null,
  children: React.Node,
};

export type Store = {
  liveUsers: User[],
  lots: Lot[],
  auction: Auction | null,
  currentLot: Lot | null,
  showSoldMessage: boolean,
  streamUrl: string | null,
  streamStatus: string,
};

type State = {
  store: Store,
};

const RealtimeContext = React.createContext();

class RealtimeDataProvider extends React.PureComponent<Props, State> {
  audioPlayer: HTMLAudioElement;

  broadcasterCallObject: any;

  listenerCallObject: any;

  socket: any;

  constructor() {
    super();
    // $FlowFixMe
    this.audioPlayer = new Audio(gunSound);
    this.broadcasterCallObject = null;
    this.listenerCallObject = null;
    this.socket = null;
  }

  state = {
    store: {
      liveUsers: [],
      lots: [],
      auction: null,
      currentLot: null,
      showSoldMessage: false,
      streamUrl: null,
      streamStatus: STREAM_STATUS.LOADING,
      isBroadcaster: false,
    },
  };

  componentDidMount() {
    const self = this;
    const { token, preloadedStreamUrl } = this.props;

    const auctionId = String(auctionService.getAuctionId());
    // $FlowFixMe
    this.socket = io(process.env.REACT_APP_ROUNDUP_API_URL, {
      path: '/api/realtime',
      query: { auctionId },
      transportOptions: {
        polling: {
          extraHeaders: {
            Authorization: `Bearer ${token}`,
          },
        },
      },
    });
    this.socket.on(EVENT_TYPES.LIVE_USERS, this.handleLiveUsersEvent);
    this.socket.on(EVENT_TYPES.AUCTION, this.handleAuctionEvent);
    this.socket.on(EVENT_TYPES.STREAM_STARTED, this.handleStreamStarted);
    this.socket.on(EVENT_TYPES.STREAM_STOPPED, this.handleStreamStopped);

    // If stream was active before login, auto-join immediately (within login gesture chain)
    if (preloadedStreamUrl) {
      self.autoJoinStream(preloadedStreamUrl);
    }
  }

  componentWillUnmount() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    this.cleanupAllCallObjects();
  }

  cleanupCallObject = function(callObject) {
    if (callObject) {
      return callObject
        .leave()
        .then(() => {
          return callObject.destroy();
        })
        .catch(err => {
          console.error('Error during Daily.co cleanup:', err);
        });
    }
    return Promise.resolve();
  };

  cleanupAllCallObjects = function() {
    var broadcasterCo = this.broadcasterCallObject;
    var listenerCo = this.listenerCallObject;
    this.broadcasterCallObject = null;
    this.listenerCallObject = null;
    return Promise.all([this.cleanupCallObject(broadcasterCo), this.cleanupCallObject(listenerCo)]);
  };

  autoJoinStream = function(roomUrl) {
    const self = this;
    self.setState(state => {
      return {
        store: Object.assign({}, state.store, {
          streamUrl: roomUrl,
          streamStatus: STREAM_STATUS.CONNECTING,
        }),
      };
    });

    const co = DailyIframe.createCallObject({
      subscribeToTracksAutomatically: true,
      audioSource: false,
      videoSource: false,
    });
    self.listenerCallObject = co;

    var isAdmin = self.props.user && hasRole(self.props.user.roles, ROLES.ADMIN);

    co.join({ url: roomUrl })
      .then(() => {
        self.setState(state => {
          return {
            store: Object.assign({}, state.store, {
              streamStatus: isAdmin ? STREAM_STATUS.ADMIN_LISTENING : STREAM_STATUS.LISTENING,
            }),
          };
        });
      })
      .catch(err => {
        console.error('Failed to auto-join stream:', err);
        self.listenerCallObject = null;
        self.cleanupCallObject(co);
        toast.error(<Text t="stream.joinFailed" />);
        self.setState(state => {
          return {
            store: Object.assign({}, state.store, {
              streamStatus: STREAM_STATUS.REQUIRES_INTERACTION,
            }),
          };
        });
      });
  };

  // --- Action methods exposed via context ---

  joinStream = function() {
    const self = this;
    const { streamUrl } = self.state.store;
    if (!streamUrl || self.state.store.streamStatus !== STREAM_STATUS.REQUIRES_INTERACTION) {
      return;
    }

    self.setState(state => {
      return {
        store: Object.assign({}, state.store, {
          streamStatus: STREAM_STATUS.CONNECTING,
        }),
      };
    });

    const co = DailyIframe.createCallObject({
      subscribeToTracksAutomatically: true,
      audioSource: false,
      videoSource: false,
    });
    self.listenerCallObject = co;

    co.join({ url: streamUrl })
      .then(() => {
        self.setState(state => {
          return {
            store: Object.assign({}, state.store, {
              streamStatus: STREAM_STATUS.LISTENING,
            }),
          };
        });
      })
      .catch(err => {
        console.error('Failed to join stream:', err);
        self.listenerCallObject = null;
        self.cleanupCallObject(co);
        toast.error(<Text t="stream.joinFailed" />);
        self.setState(state => {
          return {
            store: Object.assign({}, state.store, {
              streamStatus: STREAM_STATUS.REQUIRES_INTERACTION,
            }),
          };
        });
      });
  };

  startBroadcast = function() {
    const self = this;
    if (self.state.store.streamStatus !== STREAM_STATUS.NO_STREAM) {
      return;
    }

    // Transition immediately to prevent double-click
    self.setState(state => {
      return {
        store: Object.assign({}, state.store, {
          streamStatus: STREAM_STATUS.CONNECTING,
          isBroadcaster: true,
        }),
      };
    });

    const auctionId = auctionService.getAuctionId();

    auctionService
      .startStream(auctionId)
      .then(response => {
        const { roomUrl, token } = response.data;

        const co = DailyIframe.createCallObject({
          audioSource: true,
          videoSource: false,
        });
        self.broadcasterCallObject = co;

        co.on('error', e => {
          console.error('Daily.co error (broadcaster):', e);
        });
        co.on('camera-error', e => {
          console.error('Daily.co camera/mic error:', e);
        });

        return co.join({ url: roomUrl, token: token }).then(() => {
          self.setState(state => {
            return {
              store: Object.assign({}, state.store, {
                streamUrl: roomUrl,
                streamStatus: STREAM_STATUS.BROADCASTING,
              }),
            };
          });
        });
      })
      .catch(err => {
        console.error('Failed to start broadcast:', err);
        self.cleanupCallObject(self.broadcasterCallObject);
        self.broadcasterCallObject = null;
        self.setState(state => {
          return {
            store: Object.assign({}, state.store, {
              streamStatus: STREAM_STATUS.NO_STREAM,
              isBroadcaster: false,
            }),
          };
        });
      });
  };

  stopStream = function() {
    var self = this;
    var auctionId = auctionService.getAuctionId();

    // Optimistically transition — user intended to stop, so show NO_STREAM immediately
    self.setState(function(state) {
      return {
        store: Object.assign({}, state.store, {
          streamUrl: null,
          streamStatus: STREAM_STATUS.NO_STREAM,
          isBroadcaster: false,
        }),
      };
    });

    self.cleanupAllCallObjects();

    auctionService
      .stopStream(auctionId)
      .then(function() {
        // STREAM_STOPPED socket event will arrive, but we've already transitioned.
        // handleStreamStopped is idempotent — setting NO_STREAM when already NO_STREAM is harmless.
      })
      .catch(function(err) {
        console.error('Failed to stop stream:', err);
      });
  };

  startAdminListen = function() {
    const self = this;
    if (self.state.store.streamStatus !== STREAM_STATUS.REQUIRES_INTERACTION) {
      return;
    }

    // Transition immediately to prevent double-click
    self.setState(state => {
      return {
        store: Object.assign({}, state.store, {
          streamStatus: STREAM_STATUS.CONNECTING,
        }),
      };
    });

    const auctionId = auctionService.getAuctionId();

    auctionService
      .listenStream(auctionId)
      .then(response => {
        const { roomUrl, token } = response.data;

        const co = DailyIframe.createCallObject({
          subscribeToTracksAutomatically: true,
          audioSource: false,
          videoSource: false,
        });
        self.listenerCallObject = co;

        return co.join({ url: roomUrl, token: token }).then(() => {
          self.setState(state => {
            return {
              store: Object.assign({}, state.store, {
                streamStatus: STREAM_STATUS.ADMIN_LISTENING,
              }),
            };
          });
        });
      })
      .catch(err => {
        console.error('Failed to start admin listen:', err);
        self.cleanupCallObject(self.listenerCallObject);
        self.listenerCallObject = null;
        self.setState(state => {
          return {
            store: Object.assign({}, state.store, {
              streamStatus: STREAM_STATUS.REQUIRES_INTERACTION,
            }),
          };
        });
      });
  };

  getListenerCallObject = function() {
    return this.listenerCallObject;
  };

  // --- Socket event handlers ---

  handleLiveUsersEvent = (liveUsers: User[]) => {
    this.setState(state => ({
      ...state,
      store: { ...state.store, liveUsers },
    }));
  };

  handleAuctionEvent = ({ auction, lots, currentLot, meta }) => {
    if (meta && meta.event) {
      switch (meta.event) {
        case EVENT_TYPES.LOT_STATUS_CHANGED:
          this.handleLotStatusChangeEvent(meta);
          break;
        case EVENT_TYPES.BID_PLACED:
          this.playBangSound();
          break;
        default:
          break;
      }
    }

    this.setState(state => ({
      ...state,
      store: { ...state.store, auction, lots, currentLot },
    }));

    // If still LOADING and no preloaded stream, transition to NO_STREAM.
    // If a stream exists, the server sends STREAM_STARTED right after AUCTION.
    // We use a microtask to let STREAM_STARTED arrive first if it exists.
    const self = this;
    if (self.state.store.streamStatus === STREAM_STATUS.LOADING && !self.props.preloadedStreamUrl) {
      Promise.resolve().then(() => {
        if (self.state.store.streamStatus === STREAM_STATUS.LOADING) {
          self.setState(state => {
            return {
              store: Object.assign({}, state.store, {
                streamStatus: STREAM_STATUS.NO_STREAM,
              }),
            };
          });
        }
      });
    }
  };

  handleLotStatusChangeEvent = ({ lot, status }) => {
    const { user } = this.props;

    if (status === 'sold') {
      this.setState(state => ({
        ...state,
        store: { ...state.store, showSoldMessage: true },
      }));
      return;
    }

    if (status === 'clearSold') {
      this.setState(state => ({
        ...state,
        store: { ...state.store, showSoldMessage: false },
      }));
      return;
    }

    const userIsNotAdmin = !hasRole(user.roles, ROLES.ADMIN);
    if (userIsNotAdmin) {
      toast.info(`Lot ${lot.lotNumber} was ${status}!`, {
        autoClose: 5000,
        position: toast.POSITION.TOP_CENTER,
        className: classes.lotStatusToast,
      });
    }
  };

  playBangSound = () => {
    if (this.audioPlayer) {
      this.audioPlayer.play();
    }
  };

  handleStreamStarted = ({ roomUrl }) => {
    const self = this;
    const currentStatus = self.state.store.streamStatus;
    const currentUrl = self.state.store.streamUrl;

    // We are the broadcaster — startBroadcast() will handle the state transition
    if (self.state.store.isBroadcaster) {
      return;
    }

    // If already listening/connecting to a different stream, require interaction for the new one
    if (
      (currentStatus === STREAM_STATUS.LISTENING || currentStatus === STREAM_STATUS.CONNECTING) &&
      currentUrl !== roomUrl
    ) {
      self.cleanupCallObject(self.listenerCallObject);
      self.listenerCallObject = null;
      self.setState(state => {
        return {
          store: Object.assign({}, state.store, {
            streamUrl: roomUrl,
            streamStatus: STREAM_STATUS.REQUIRES_INTERACTION,
          }),
        };
      });
      return;
    }

    // If loading and we have a preloaded URL that matches, the auto-join is already in progress
    if (currentStatus === STREAM_STATUS.CONNECTING && currentUrl === roomUrl) {
      return;
    }

    // If we're broadcasting, ignore the event (we started it)
    if (currentStatus === STREAM_STATUS.BROADCASTING) {
      return;
    }

    // If loading with no preloaded URL, or no stream → requires interaction
    if (currentStatus === STREAM_STATUS.LOADING || currentStatus === STREAM_STATUS.NO_STREAM) {
      self.setState(state => {
        return {
          store: Object.assign({}, state.store, {
            streamUrl: roomUrl,
            streamStatus: STREAM_STATUS.REQUIRES_INTERACTION,
          }),
        };
      });
    }
  };

  handleStreamStopped = () => {
    this.cleanupAllCallObjects();
    this.setState(state => {
      return {
        store: Object.assign({}, state.store, {
          streamUrl: null,
          streamStatus: STREAM_STATUS.NO_STREAM,
          isBroadcaster: false,
        }),
      };
    });
  };

  render() {
    const self = this;
    const contextValue = {
      store: this.state.store,
      joinStream: function() {
        self.joinStream();
      },
      startBroadcast: function() {
        self.startBroadcast();
      },
      stopStream: function() {
        self.stopStream();
      },
      startAdminListen: function() {
        self.startAdminListen();
      },
      getListenerCallObject: function() {
        return self.getListenerCallObject();
      },
    };

    return <RealtimeContext.Provider value={contextValue}>{this.props.children}</RealtimeContext.Provider>;
  }
}

// $FlowFixMe
export const withRealtimeData = mapStateToProps => Component => {
  // $FlowFixMe
  return function RealtimeComponent(props) {
    return (
      <RealtimeContext.Consumer>
        {/*  $FlowFixMe */}
        {({ store, joinStream, startBroadcast, stopStream, startAdminListen, getListenerCallObject }) => (
          <Component
            {...props}
            {...mapStateToProps(store, props)}
            joinStream={joinStream}
            startBroadcast={startBroadcast}
            stopStream={stopStream}
            startAdminListen={startAdminListen}
            getListenerCallObject={getListenerCallObject}
          />
        )}
      </RealtimeContext.Consumer>
    );
  };
};

export default withCredentials(RealtimeDataProvider);
